/**
 * KommunikationSettingsNew - Kommunikationskanäle Einstellungen
 * 5 Unterreiter: E-Mail, Fax, EGVP, WhatsApp, Telefon
 * + Webhook-URLs Übersicht
 */

import { useState, useEffect } from 'react';
import {
  Mail, Printer, Building2, MessageCircle, Phone,
  Plus, Trash2, Eye, EyeOff, RefreshCw, CheckCircle2,
  AlertCircle, Copy, ExternalLink, Upload, Link2, X, Save, Loader2,
  Key, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Kanal-Definitionen
const KANAELE = [
  { key: 'email', label: 'E-Mail', icon: Mail, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  { key: 'fax', label: 'Fax', icon: Printer, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  { key: 'egvp', label: 'EGVP', icon: Building2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-green-600', bgColor: 'bg-green-50' },
  { key: 'telefon', label: 'Telefon', icon: Phone, color: 'text-teal-600', bgColor: 'bg-teal-50' },
];

export default function KommunikationSettingsNew() {
  const [status, setStatus] = useState({});
  const [activeTab, setActiveTab] = useState('email');
  const [loading, setLoading] = useState(true);
  const [placetelKey, setPlacetelKey] = useState('');
  const [placetelKeyExists, setPlacetelKeyExists] = useState(false);
  const [savingPlacetel, setSavingPlacetel] = useState(false);

  useEffect(() => { loadStatus(); loadPlacetelKey(); }, []);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/api/komm/status`);
      if (res.ok) setStatus(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadPlacetelKey = async () => {
    try {
      const res = await fetch(`${API}/api/komm/placetel-key-status`);
      if (res.ok) {
        const data = await res.json();
        setPlacetelKeyExists(data.vorhanden);
      }
    } catch (e) { console.error(e); }
  };

  const savePlacetelKey = async () => {
    if (!placetelKey) return;
    setSavingPlacetel(true);
    try {
      const res = await fetch(`${API}/api/komm/placetel-key`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: placetelKey }),
      });
      if (res.ok) {
        toast.success('Placetel API-Key gespeichert (Fax + Telefon)');
        setPlacetelKeyExists(true);
        setPlacetelKey('');
        loadStatus();
      }
    } catch (e) { toast.error('Fehler beim Speichern'); }
    setSavingPlacetel(false);
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Laden...</div>;

  return (
    <div className="space-y-6" data-testid="komm-settings">
      {/* Gemeinsamer Placetel API-Key */}
      <Card className="border-teal-200 bg-teal-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-teal-600" />
            Placetel API-Key (Fax + Telefon gemeinsam)
          </CardTitle>
          <CardDescription>Ein API-Key für alle Placetel-Dienste: Fax, Telefon, Click-to-Call</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            {placetelKeyExists ? (
              <Badge className="bg-green-100 text-green-700">API-Key hinterlegt</Badge>
            ) : (
              <Badge variant="secondary" className="text-slate-500">Nicht konfiguriert</Badge>
            )}
            <SecretField
              label=""
              value={placetelKey}
              onChange={setPlacetelKey}
              placeholder={placetelKeyExists ? 'Neuen Key eingeben um zu ersetzen...' : 'Placetel API-Key eingeben'}
              testId="placetel-api-key"
            />
            <Button onClick={savePlacetelKey} disabled={savingPlacetel || !placetelKey} size="sm" data-testid="save-placetel-key">
              {savingPlacetel ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          {KANAELE.map(k => (
            <TabsTrigger key={k.key} value={k.key} className="flex items-center gap-1.5 text-xs" data-testid={`tab-${k.key}`}>
              <k.icon className={`w-3.5 h-3.5 ${status[k.key]?.aktiv ? k.color : 'text-slate-400'}`} />
              <span className="hidden sm:inline">{k.label}</span>
              {status[k.key]?.aktiv ? (
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
              ) : (
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
              )}
            </TabsTrigger>
          ))}
          <TabsTrigger value="webhooks" className="flex items-center gap-1.5 text-xs" data-testid="tab-webhooks">
            <Link2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Webhooks</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email"><EmailSettings status={status.email} onRefresh={loadStatus} /></TabsContent>
        <TabsContent value="fax"><FaxSettings status={status.fax} onRefresh={loadStatus} placetelKeyExists={placetelKeyExists} /></TabsContent>
        <TabsContent value="egvp"><EGVPSettings status={status.egvp} onRefresh={loadStatus} /></TabsContent>
        <TabsContent value="whatsapp"><WhatsAppSettings status={status.whatsapp} onRefresh={loadStatus} /></TabsContent>
        <TabsContent value="telefon"><TelefonSettings status={status.telefon} onRefresh={loadStatus} placetelKeyExists={placetelKeyExists} /></TabsContent>
        <TabsContent value="webhooks"><WebhookURLs /></TabsContent>
      </Tabs>
    </div>
  );
}

// ==================== SHARED COMPONENTS ====================

function StatusBadge({ aktiv, mockMode }) {
  if (mockMode) return <Badge variant="outline" className="text-amber-600 border-amber-300">Mock-Modus</Badge>;
  if (aktiv) return <Badge className="bg-green-100 text-green-700">Aktiv</Badge>;
  return <Badge variant="secondary" className="text-slate-500">Nicht konfiguriert</Badge>;
}

function TestButton({ kanal, onRefresh }) {
  const [testing, setTesting] = useState(false);
  const test = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/komm/test/${kanal}`, { method: 'POST' });
      const data = await res.json();
      if (data.success) toast.success(data.message);
      else toast.error(data.error);
    } catch (e) { toast.error('Test fehlgeschlagen'); }
    setTesting(false);
    onRefresh?.();
  };
  return (
    <Button variant="outline" size="sm" onClick={test} disabled={testing} data-testid={`test-${kanal}`}>
      {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
      Verbindung testen
    </Button>
  );
}

function SecretField({ label, value, onChange, placeholder, testId }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-1">
        <Input
          type={show ? 'text' : 'password'}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
        />
        <Button variant="ghost" size="sm" onClick={() => setShow(!show)} className="shrink-0 px-2">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// ==================== E-MAIL SETTINGS ====================

function EmailSettings({ status, onRefresh }) {
  const [konten, setKonten] = useState([]);
  const [editKonto, setEditKonto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [sig, setSig] = useState({ name: '', position: '', telefon: '', kanzlei: '', disclaimer: '', aktiv: false });
  const [sigSaving, setSigSaving] = useState(false);
  const [sigPreview, setSigPreview] = useState('');

  useEffect(() => { loadKonten(); loadSignatur(); }, []);

  const loadKonten = async () => {
    try {
      const res = await fetch(`${API}/api/komm/config/email`);
      if (res.ok) {
        const data = await res.json();
        setKonten(data.konten || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadSignatur = async () => {
    try {
      const res = await fetch(`${API}/api/komm/signatur`);
      if (res.ok) {
        const data = await res.json();
        setSig(data);
        setSigPreview(data.text || '');
      }
    } catch (e) { console.error(e); }
  };

  const saveKonto = async () => {
    if (!editKonto) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/config/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editKonto),
      });
      if (res.ok) {
        toast.success('E-Mail-Konto gespeichert');
        setEditKonto(null);
        loadKonten();
        onRefresh();
      } else toast.error('Fehler beim Speichern');
    } catch (e) { toast.error('Fehler'); }
    setSaving(false);
  };

  const deleteKonto = async (id) => {
    await fetch(`${API}/api/komm/config/email/${id}`, { method: 'DELETE' });
    toast.success('Konto gelöscht');
    loadKonten();
    onRefresh();
  };

  const fetchEmails = async (kontoId) => {
    setFetching(true);
    try {
      const url = kontoId ? `${API}/api/komm/fetch/email?konto_id=${kontoId}` : `${API}/api/komm/fetch/email`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        toast.success(`${data.neue_emails} neue E-Mail(s) abgerufen`);
      } else {
        toast.error(data.errors?.[0] || 'Fehler beim Abrufen');
      }
    } catch (e) { toast.error('Abruf fehlgeschlagen'); }
    setFetching(false);
  };

  const saveSignatur = async () => {
    setSigSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/signatur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sig),
      });
      if (res.ok) {
        const data = await res.json();
        setSigPreview(data.text || '');
        toast.success('Signatur gespeichert');
      }
    } catch (e) { toast.error('Fehler'); }
    setSigSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-purple-600" /> E-Mail-Konten (IMAP/SMTP)</CardTitle>
            <CardDescription>Mehrere E-Mail-Konten für den Posteingang und Versand</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge aktiv={status?.aktiv} />
            <Button variant="outline" size="sm" onClick={() => fetchEmails()} disabled={fetching || konten.length === 0} data-testid="fetch-emails-btn">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              E-Mails abrufen
            </Button>
            <TestButton kanal="email" onRefresh={onRefresh} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bestehende Konten */}
        {konten.map(k => (
          <div key={k.id} className="flex items-center justify-between p-3 border rounded-lg bg-purple-50/30">
            <div>
              <p className="font-medium text-sm">{k.name || k.username}</p>
              <p className="text-xs text-slate-500">{k.imap_server} / {k.smtp_server}</p>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => setEditKonto({...k})}><Eye className="w-4 h-4" /></Button>
              <Button variant="ghost" size="sm" onClick={() => deleteKonto(k.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          </div>
        ))}

        {/* Neues Konto / Bearbeiten */}
        {editKonto ? (
          <div className="border rounded-lg p-4 space-y-3 bg-white">
            <div className="flex justify-between items-center">
              <h4 className="font-medium">{editKonto.id ? 'Konto bearbeiten' : 'Neues E-Mail-Konto'}</h4>
              <Button variant="ghost" size="sm" onClick={() => setEditKonto(null)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Kontoname</Label><Input value={editKonto.name || ''} onChange={e => setEditKonto({...editKonto, name: e.target.value})} placeholder="z.B. Büro E-Mail" data-testid="email-name" /></div>
              <div><Label>IMAP-Server</Label><Input value={editKonto.imap_server || ''} onChange={e => setEditKonto({...editKonto, imap_server: e.target.value})} placeholder="imap.gmail.com" data-testid="email-imap" /></div>
              <div><Label>IMAP-Port</Label><Input type="number" value={editKonto.imap_port || 993} onChange={e => setEditKonto({...editKonto, imap_port: parseInt(e.target.value)})} /></div>
              <div><Label>SMTP-Server</Label><Input value={editKonto.smtp_server || ''} onChange={e => setEditKonto({...editKonto, smtp_server: e.target.value})} placeholder="smtp.gmail.com" data-testid="email-smtp" /></div>
              <div><Label>SMTP-Port</Label><Input type="number" value={editKonto.smtp_port || 587} onChange={e => setEditKonto({...editKonto, smtp_port: parseInt(e.target.value)})} /></div>
              <div><Label>Benutzername</Label><Input value={editKonto.username || ''} onChange={e => setEditKonto({...editKonto, username: e.target.value})} placeholder="user@example.com" data-testid="email-user" /></div>
              <SecretField label="Passwort" value={editKonto.password || ''} onChange={v => setEditKonto({...editKonto, password: v})} placeholder="Passwort oder App-Kennwort" testId="email-password" />
              <div><Label>Absendername</Label><Input value={editKonto.from_name || ''} onChange={e => setEditKonto({...editKonto, from_name: e.target.value})} placeholder="Betreuungsbüro" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditKonto(null)}>Abbrechen</Button>
              <Button onClick={saveKonto} disabled={saving} data-testid="email-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Speichern
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setEditKonto({ imap_port: 993, smtp_port: 587, imap_use_ssl: true, smtp_use_tls: true })} data-testid="add-email-btn">
            <Plus className="w-4 h-4 mr-1" /> E-Mail-Konto hinzufügen
          </Button>
        )}

        {/* E-Mail Signatur */}
        <div className="border-t border-slate-200 pt-4 mt-4">
          <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-purple-500" />
            E-Mail-Signatur
            <label className="ml-auto flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-slate-500 font-normal">{sig.aktiv ? 'Aktiv' : 'Inaktiv'}</span>
              <input
                type="checkbox"
                checked={sig.aktiv || false}
                onChange={e => setSig({ ...sig, aktiv: e.target.checked })}
                className="w-4 h-4 accent-purple-600"
                data-testid="sig-toggle"
              />
            </label>
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Name</Label><Input value={sig.name || ''} onChange={e => setSig({...sig, name: e.target.value})} placeholder="Max Mustermann" data-testid="sig-name" /></div>
            <div><Label className="text-xs">Position / Rolle</Label><Input value={sig.position || ''} onChange={e => setSig({...sig, position: e.target.value})} placeholder="Berufsbetreuer" data-testid="sig-position" /></div>
            <div><Label className="text-xs">Kanzlei / Büro</Label><Input value={sig.kanzlei || ''} onChange={e => setSig({...sig, kanzlei: e.target.value})} placeholder="Betreuungsbüro Mustermann" data-testid="sig-kanzlei" /></div>
            <div><Label className="text-xs">Telefon</Label><Input value={sig.telefon || ''} onChange={e => setSig({...sig, telefon: e.target.value})} placeholder="+49 89 123456" data-testid="sig-telefon" /></div>
            <div className="col-span-2"><Label className="text-xs">Disclaimer / Hinweis</Label><Input value={sig.disclaimer || ''} onChange={e => setSig({...sig, disclaimer: e.target.value})} placeholder="Diese E-Mail kann vertrauliche Informationen enthalten..." data-testid="sig-disclaimer" /></div>
          </div>

          {sigPreview && (
            <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-[10px] text-slate-400 mb-1 uppercase tracking-wide">Vorschau</p>
              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed">{sigPreview}</pre>
            </div>
          )}

          <div className="flex justify-end mt-3">
            <Button onClick={saveSignatur} disabled={sigSaving} size="sm" data-testid="sig-save">
              {sigSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Signatur speichern
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== FAX SETTINGS ====================

function FaxSettings({ status, onRefresh, placetelKeyExists }) {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API}/api/komm/config/fax`);
      if (res.ok) { const d = await res.json(); setCfg(d.settings || {}); }
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/config/fax`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cfg, aktiv: true }),
      });
      if (res.ok) { toast.success('Fax-Einstellungen gespeichert'); onRefresh(); }
    } catch (e) { toast.error('Fehler'); }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Printer className="w-5 h-5 text-amber-600" /> Digitales Fax (Placetel)</CardTitle>
            <CardDescription>Fax senden und empfangen über Placetel API</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge aktiv={status?.aktiv || placetelKeyExists} />
            <TestButton kanal="fax" onRefresh={onRefresh} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {placetelKeyExists ? (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> Placetel API-Key ist hinterlegt (gemeinsam mit Telefon)
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> Bitte oben den gemeinsamen Placetel API-Key hinterlegen
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Fax-Nummer</Label><Input value={cfg.fax_nummer || ''} onChange={e => setCfg({...cfg, fax_nummer: e.target.value})} placeholder="+49 89 123456" data-testid="fax-nummer" /></div>
          <div><Label>Absendername (optional)</Label><Input value={cfg.fax_absender || ''} onChange={e => setCfg({...cfg, fax_absender: e.target.value})} placeholder="Betreuungsbüro Mustermann" /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="fax-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== EGVP SETTINGS ====================

function EGVPSettings({ status, onRefresh }) {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API}/api/komm/config/egvp`);
      if (res.ok) { const d = await res.json(); setCfg(d.settings || {}); }
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/config/egvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cfg, mock_mode: cfg.mock_mode !== false }),
      });
      if (res.ok) { toast.success('EGVP-Einstellungen gespeichert'); onRefresh(); }
    } catch (e) { toast.error('Fehler'); }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-600" /> EGVP (Governikus)</CardTitle>
            <CardDescription>Elektronisches Gerichts- und Verwaltungspostfach</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge aktiv={status?.aktiv} mockMode={status?.mock_mode} />
            <TestButton kanal="egvp" onRefresh={onRefresh} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
          <AlertCircle className="w-4 h-4 shrink-0" /> Mock-Modus aktiv — Alle Felder vorbereitet. Live-Anbindung erfolgt nach Konfiguration der Zugangsdaten.
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>SAFE-ID</Label><Input value={cfg.safe_id || ''} onChange={e => setCfg({...cfg, safe_id: e.target.value})} placeholder="de.betreuer.max-mustermann" data-testid="egvp-safe-id" /></div>
          <div><Label>EGVP-Gateway URL</Label><Input value={cfg.gateway_url || ''} onChange={e => setCfg({...cfg, gateway_url: e.target.value})} placeholder="https://egvp-gateway.justiz.de" data-testid="egvp-gateway" /></div>
          <div><Label>Eigene Postfach-Adresse</Label><Input value={cfg.postfach_adresse || ''} onChange={e => setCfg({...cfg, postfach_adresse: e.target.value})} placeholder="postfach@egvp.de" data-testid="egvp-postfach" /></div>
          <SecretField label="Zertifikat-Passwort" value={cfg.zertifikat_passwort || ''} onChange={v => setCfg({...cfg, zertifikat_passwort: v})} placeholder="Passwort für .p12-Zertifikat" testId="egvp-cert-pw" />
        </div>
        <div>
          <Label>Governikus-Zertifikat (.p12)</Label>
          <div className="flex items-center gap-2 mt-1">
            {cfg.zertifikat_vorhanden ? (
              <Badge className="bg-green-100 text-green-700">Zertifikat hochgeladen</Badge>
            ) : (
              <Badge variant="secondary">Kein Zertifikat</Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => document.getElementById('egvp-cert-upload').click()} data-testid="egvp-upload-cert">
              <Upload className="w-4 h-4 mr-1" /> Zertifikat hochladen
            </Button>
            <input id="egvp-cert-upload" type="file" accept=".p12" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const formData = new FormData();
              formData.append('zertifikat', file);
              formData.append('zertifikat_passwort', cfg.zertifikat_passwort || '');
              try {
                const res = await fetch(`${API}/api/komm/config/egvp/zertifikat`, { method: 'POST', body: formData });
                if (res.ok) { toast.success('Zertifikat hochgeladen'); loadConfig(); }
              } catch (err) { toast.error('Upload fehlgeschlagen'); }
            }} />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={cfg.mock_mode !== false} onCheckedChange={(v) => setCfg({...cfg, mock_mode: v})} />
          <Label className="text-sm">Mock-Modus (keine echten Nachrichten)</Label>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="egvp-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== WHATSAPP SETTINGS ====================

function WhatsAppSettings({ status, onRefresh }) {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API}/api/komm/config/whatsapp`);
      if (res.ok) { const d = await res.json(); setCfg(d.settings || {}); }
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/config/whatsapp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cfg, aktiv: true }),
      });
      if (res.ok) { toast.success('WhatsApp-Einstellungen gespeichert'); onRefresh(); }
    } catch (e) { toast.error('Fehler'); }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-green-600" /> WhatsApp Business</CardTitle>
            <CardDescription>WhatsApp-Nachrichten empfangen und versenden via Meta Cloud API</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge aktiv={status?.aktiv} />
            <TestButton kanal="whatsapp" onRefresh={onRefresh} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Business Account ID</Label><Input value={cfg.business_account_id || ''} onChange={e => setCfg({...cfg, business_account_id: e.target.value})} placeholder="123456789012345" data-testid="wa-account-id" /></div>
          <div><Label>Phone Number ID</Label><Input value={cfg.phone_number_id || ''} onChange={e => setCfg({...cfg, phone_number_id: e.target.value})} placeholder="123456789012345" data-testid="wa-phone-id" /></div>
          <SecretField label="Access Token" value={cfg.access_token || ''} onChange={v => setCfg({...cfg, access_token: v})} placeholder="EAAGm..." testId="wa-token" />
          <div><Label>WhatsApp-Nummer</Label><Input value={cfg.whatsapp_nummer || ''} onChange={e => setCfg({...cfg, whatsapp_nummer: e.target.value})} placeholder="+49 170 1234567" data-testid="wa-nummer" /></div>
        </div>
        {!status?.aktiv && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> Zugangsdaten erforderlich — Meta Developer Portal: developers.facebook.com
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="wa-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== TELEFON SETTINGS ====================

function TelefonSettings({ status, onRefresh, placetelKeyExists }) {
  const [cfg, setCfg] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);
  const loadConfig = async () => {
    try {
      const res = await fetch(`${API}/api/komm/config/telefon`);
      if (res.ok) { const d = await res.json(); setCfg(d.settings || {}); }
    } catch (e) { console.error(e); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/komm/config/telefon`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...cfg, aktiv: true }),
      });
      if (res.ok) { toast.success('Telefon-Einstellungen gespeichert'); onRefresh(); }
    } catch (e) { toast.error('Fehler'); }
    setSaving(false);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-teal-600" /> Telefon (Placetel)</CardTitle>
            <CardDescription>Click-to-Call, Anrufprotokoll und automatische Zuordnung</CardDescription>
          </div>
          <div className="flex gap-2">
            <StatusBadge aktiv={status?.aktiv || placetelKeyExists} />
            <TestButton kanal="telefon" onRefresh={onRefresh} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {placetelKeyExists ? (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-700">
            <CheckCircle2 className="w-4 h-4 shrink-0" /> Placetel API-Key ist hinterlegt (gemeinsam mit Fax)
          </div>
        ) : (
          <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
            <AlertCircle className="w-4 h-4 shrink-0" /> Bitte oben den gemeinsamen Placetel API-Key hinterlegen
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div><Label>SIP-User</Label><Input value={cfg.sip_user || ''} onChange={e => setCfg({...cfg, sip_user: e.target.value})} placeholder="777888@sip.placetel.de" data-testid="tel-sip-user" /></div>
          <SecretField label="SIP-Passwort" value={cfg.sip_passwort || ''} onChange={v => setCfg({...cfg, sip_passwort: v})} placeholder="SIP-Passwort" testId="tel-sip-pw" />
          <div><Label>SIP-Domain</Label><Input value={cfg.sip_domain || ''} onChange={e => setCfg({...cfg, sip_domain: e.target.value})} placeholder="sip.placetel.de" data-testid="tel-sip-domain" /></div>
          <div><Label>Eigene Rufnummer</Label><Input value={cfg.eigene_nummer || ''} onChange={e => setCfg({...cfg, eigene_nummer: e.target.value})} placeholder="+49 89 123456" data-testid="tel-eigene-nummer" /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="tel-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== WEBHOOK URLS ====================

function WebhookURLs() {
  const [webhooks, setWebhooks] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/komm/webhook-urls`)
      .then(r => r.json())
      .then(d => setWebhooks(d.webhooks || []))
      .catch(console.error);
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL kopiert');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> Webhook-URLs</CardTitle>
        <CardDescription>Diese URLs bei den jeweiligen Diensten als Webhook hinterlegen</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4" data-testid="webhook-urls">
        {webhooks.map((wh, i) => (
          <div key={i} className="p-4 border rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline">{wh.dienst}</Badge>
                <span className="font-medium text-sm">{wh.typ}</span>
              </div>
              <Badge variant="secondary" className="text-xs">{wh.methode}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-100 px-3 py-2 rounded text-xs font-mono text-slate-700 break-all" data-testid={`webhook-url-${i}`}>
                {wh.url}
              </code>
              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(wh.url)} data-testid={`copy-webhook-${i}`}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500">{wh.hinweis}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
